import { useUserRoles } from '@anytag/auth';
import { Form } from '@anytag/shared-atoms';
import { useQueryHelper, useToggleState } from '@anytag/shared-hooks';
import {
  EngagementCampaignStatus,
  ProposalJobDescriptionBreakDownMode,
  useUpdateEngagementBreakdownModeMutation
} from '@anytag/graphql';
import { TableTitle } from '../../shared';
import { formatEngagementProposalData } from '../utils';
import { EngagementProposalInfluencersFormValues } from '../types';
import { useEngagementProposalSearch, useUpdateInfluencerAccountsBudget } from '../hooks';
import { TableModes } from './TableModes';
import { LeaveConfirmModal } from './LeaveConfirmModal';
import { SelectedInfluencersTable, SelectedInfluencersTableProps } from './SelectedInfluencersTable';

type SelectedInfluencersProps = Omit<SelectedInfluencersTableProps, 'data'>;

export const SelectedInfluencers = ({
  jdMode,
  engagement,
  selectedInfluencers,
  hasTrackingReferral,
  formattedInfluencers,
  formattedSocialAccounts,
  hasInfluencerCostPermission
}: SelectedInfluencersProps) => {
  const { id, status } = engagement;
  const confirmModal = useToggleState();
  const { t, enqueueSnackbar, invalidateRouteLoader } = useQueryHelper();
  const { isAdminStaffTalentAgencyPartner } = useUserRoles();
  const { callUpdateEngagementBreakdownMode, loading } = useUpdateEngagementBreakdownModeMutation({
    onCompleted: invalidateRouteLoader
  });
  const { isEditMode } = useEngagementProposalSearch();
  const { updateInfluencerAccountsBudget } = useUpdateInfluencerAccountsBudget({ jdMode, engagementId: id });

  const { tableData, formValues } = formatEngagementProposalData(selectedInfluencers, jdMode, isEditMode);

  const handleJdModeChange = async (mode: ProposalJobDescriptionBreakDownMode) => {
    if (mode === jdMode) {
      return;
    }

    try {
      await callUpdateEngagementBreakdownMode({ variables: { input: { campaignId: id, jdMode: mode } } });
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  return (
    <Form.Provider<EngagementProposalInfluencersFormValues>
      values={formValues}
      onSubmit={updateInfluencerAccountsBudget(tableData)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault(); // Prevent form submit on Enter key, user needs to click the Save button
        }
      }}
    >
      <>
        <TableTitle title={t('Suggested Influencers')} />

        {selectedInfluencers.length > 0 &&
        isAdminStaffTalentAgencyPartner &&
        status !== EngagementCampaignStatus.FINISHED ? (
          <TableModes jdMode={jdMode} onEditJdMode={handleJdModeChange} onShowConfirmModal={confirmModal.open} />
        ) : null}

        <SelectedInfluencersTable
          jdMode={jdMode}
          loading={loading}
          data={tableData}
          engagement={engagement}
          selectedInfluencers={selectedInfluencers}
          formattedInfluencers={formattedInfluencers}
          formattedSocialAccounts={formattedSocialAccounts}
          hasInfluencerCostPermission={hasInfluencerCostPermission}
          hasTrackingReferral={hasTrackingReferral}
        />

        <LeaveConfirmModal isOpen={confirmModal.status} onClose={confirmModal.close} />
      </>
    </Form.Provider>
  );
};
